import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase/firestore";
import { doc, getDoc } from "firebase/firestore";
import { useUser } from "../context/userContext";

import bgImage from "../assets/bgmini.jpg";
import arrowIcon from "../assets/miniputi.svg";

// Компонент аудио плеера (без изменений)
const AudioPlayer = ({ audioData, onPlay }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState("0:00");
  const [currentTime, setCurrentTime] = useState("0:00");
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      if (!audio.duration) return;
      setProgress((audio.currentTime / audio.duration) * 100);
      
      const minutes = Math.floor(audio.currentTime / 60);
      const seconds = Math.floor(audio.currentTime % 60);
      setCurrentTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      setCurrentTime("0:00");
    };

    const onLoadedMetadata = () => {
      const minutes = Math.floor(audio.duration / 60);
      const seconds = Math.floor(audio.duration % 60);
      setDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      setIsLoaded(true);
    };

    const onError = (e) => {
      console.error('Audio error:', e);
      setError('Ошибка загрузки аудио');
      setPlaying(false);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("error", onError);

    audio.load();

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("error", onError);
      audio.pause();
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      try {
        if (onPlay) {
          await onPlay(audio, setPlaying);
        } else {
          await audio.play();
          setPlaying(true);
        }
      } catch (err) {
        console.error('Playback failed:', err);
        setError('Не удалось воспроизвести');
      }
    }
  };

  const formatFileName = (fileName) => {
    if (!fileName) return "Аудио";
    
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
    const formatted = nameWithoutExt.replace(/[_-]/g, " ");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const displayTitle = audioData.name 
    ? formatFileName(audioData.name) 
    : (audioData.title || "Аудио урок");

  if (!audioData.url) {
    return (
      <div className="w-full rounded-[20px] p-4 bg-[#241352] border border-red-500/30">
        <div className="text-red-400">Аудио файл не найден</div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-[20px] p-4 bg-[#241352] border border-[rgba(168,85,247,0.3)]">
      <div className="flex items-center gap-2">
        <button
          onClick={togglePlay}
          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity bg-[#6B3FD8] disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!!error || !isLoaded}
        >
          {playing ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
              <rect x="2" y="2" width="4" height="12" rx="1" />
              <rect x="10" y="2" width="4" height="12" rx="1" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="white">
              <path d="M5 3L17 10L5 17V3Z" />
            </svg>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="text-[16px] font-medium text-[#C8AFE8] mb-1 truncate">
            {displayTitle}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{ width: `${progress}%`, backgroundColor: '#34D3FF' }}
                />
              </div>
            </div>
            
            <div className="text-xs opacity-70 text-[#8D85D5] whitespace-nowrap">
              {playing ? currentTime : duration}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-400">
          {error}
        </div>
      )}

      <audio 
        ref={audioRef} 
        src={audioData.url} 
        preload="metadata"
        crossOrigin="anonymous"
      />
    </div>
  );
};

export default function MiniPathPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useUser();
  const [currentPage, setCurrentPage] = useState(0);
  const [miniPath, setMiniPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentData, setContentData] = useState({});
  const [error, setError] = useState(null);
  
  const activeAudioRef = useRef(null);
  const activeSetPlayingRef = useRef(null);

  // Telegram WebApp инициализация
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.disableVerticalSwipes();
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      
      window.Telegram.WebApp.BackButton.isVisible = true;
      window.Telegram.WebApp.BackButton.offClick();
      window.Telegram.WebApp.BackButton.onClick(() => {
        navigate(-1);
      });

      window.Telegram.WebApp.setBackgroundColor('#241352');
      window.Telegram.WebApp.setHeaderColor('#241352');
    }

    // Скрываем скроллбар глобально
    const style = document.createElement('style');
    style.innerHTML = `
      * {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      *::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
      }
      body {
        overflow: hidden !important;
      }
      html {
        overflow: hidden !important;
      }
    `;
    document.head.appendChild(style);

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.head.removeChild(style);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [navigate]);

  useEffect(() => {
    fetchMiniPath();
  }, [id]);

  const fetchMiniPath = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (!id) {
        setError("ID не указан");
        setLoading(false);
        return;
      }

      const pathRef = doc(db, 'miniPaths', id);
      const pathSnap = await getDoc(pathRef);
      
      if (pathSnap.exists()) {
        setMiniPath({
          id: pathSnap.id,
          ...pathSnap.data()
        });
        setLoading(false);
        return;
      }

      const trainingRef = doc(db, 'trainings', id);
      const trainingSnap = await getDoc(trainingRef);
      
      if (trainingSnap.exists()) {
        navigate(`/training/${id}`, { replace: true });
        return;
      }

      const masterClassRef = doc(db, 'masterClasses', id);
      const masterClassSnap = await getDoc(masterClassRef);
      
      if (masterClassSnap.exists()) {
        navigate(`/master-class/${id}`, { replace: true });
        return;
      }

      setError("Контент не найден");
      
    } catch (error) {
      console.error('Error fetching content:', error);
      setError("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadContentData = async () => {
      if (!miniPath?.slides || !miniPath.slides[currentPage]) return;

      const currentSlide = miniPath.slides[currentPage];
      if (!currentSlide.contents || currentSlide.contents.length === 0) return;

      const newContentData = {};
      const BASE_URL = "https://artdownload2-production.up.railway.app";

      for (const content of currentSlide.contents) {
        try {
          if (content.type === 'audio' && content.data?.id) {
            const audioDoc = await getDoc(doc(db, 'audioLessons', content.data.id));
            if (audioDoc.exists()) {
              const audioData = audioDoc.data();
              
              let audioFiles = [];
              if (audioData.audioFiles && Array.isArray(audioData.audioFiles)) {
                audioFiles = audioData.audioFiles.map((audio, index) => {
                  let audioItem = {};
                  
                  if (typeof audio === 'string') {
                    const url = audio.startsWith('/') ? `${BASE_URL}${audio}` : audio;
                    audioItem = {
                      name: `Аудио ${index + 1}`,
                      url: url,
                      title: `Аудио ${index + 1}`
                    };
                  } else if (audio && audio.url) {
                    const url = audio.url.startsWith('/') ? `${BASE_URL}${audio.url}` : audio.url;
                    audioItem = {
                      ...audio,
                      name: audio.name || audio.title || `Аудио ${index + 1}`,
                      title: audio.title || audio.name || `Аудио ${index + 1}`,
                      url: url
                    };
                  }
                  
                  return audioItem;
                }).filter(audio => audio.url);
              }
              
              newContentData[`audio_${content.id}`] = {
                ...content,
                fullData: {
                  ...audioData,
                  audioFiles: audioFiles
                }
              };
            }
          }

          if (content.type === 'training' && content.data?.id) {
            const trainingDoc = await getDoc(doc(db, 'trainings', content.data.id));
            if (trainingDoc.exists()) {
              newContentData[`training_${content.id}`] = {
                ...content,
                fullData: trainingDoc.data()
              };
            }
          }

          if (content.type === 'masterclass' && content.data?.id) {
            const masterClassDoc = await getDoc(doc(db, 'masterClasses', content.data.id));
            if (masterClassDoc.exists()) {
              newContentData[`masterclass_${content.id}`] = {
                ...content,
                fullData: masterClassDoc.data()
              };
            }
          }

          if (content.type === 'video') {
            if (content.data?.id) {
              const videoDoc = await getDoc(doc(db, 'videos', content.data.id));
              if (videoDoc.exists()) {
                newContentData[`video_${content.id}`] = {
                  ...content,
                  fullData: videoDoc.data()
                };
              } else {
                newContentData[`video_${content.id}`] = content;
              }
            } else {
              newContentData[`video_${content.id}`] = content;
            }
          }

          if (['button', 'text'].includes(content.type)) {
            newContentData[`${content.type}_${content.id}`] = content;
          }
        } catch (error) {
          console.error('Error loading content data:', error);
        }
      }

      setContentData(newContentData);
    };

    loadContentData();
  }, [currentPage, miniPath]);

  const nextPage = () => {
    if (miniPath && currentPage < miniPath.slides.length - 1) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleAudioPlay = async (audioElement, setPlayingFunction) => {
    try {
      if (activeAudioRef.current && activeAudioRef.current !== audioElement) {
        activeAudioRef.current.pause();
        if (activeSetPlayingRef.current) {
          activeSetPlayingRef.current(false);
        }
      }

      await audioElement.play();
      setPlayingFunction(true);

      activeAudioRef.current = audioElement;
      activeSetPlayingRef.current = setPlayingFunction;
    } catch (error) {
      console.error('Ошибка воспроизведения:', error);
    }
  };

  const formatText = (text) => {
    if (!text) return null;
    
    let formatted = text;
    
    formatted = formatted.replace(/^[•●]\s+(.*?)$/gm, '<li class="ml-4 text-white/90 mb-0.5">• $1</li>');
    formatted = formatted.replace(/^[-–]\s+(.*?)$/gm, '<li class="ml-4 text-white/90 mb-0.5">• $1</li>');
    formatted = formatted.replace(/^\*\s+(.*?)$/gm, '<li class="ml-4 text-white/90 mb-0.5">• $1</li>');
    
    formatted = formatted.replace(/(<li.*?>.*?<\/li>\n?)+/g, (match) => {
      return `<ul class="list-none my-1">${match}</ul>`;
    });
    
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-white">$1</strong>');
    formatted = formatted.replace(/\*(.*?)\*/g, '<em class="italic text-white">$1</em>');
    
    formatted = formatted.replace(/^### (.*?)$/gm, '<h3 class="text-lg font-bold text-white mt-3 mb-1">$1</h3>');
    formatted = formatted.replace(/^## (.*?)$/gm, '<h2 class="text-xl font-bold text-white mt-3 mb-1">$1</h2>');
    formatted = formatted.replace(/^# (.*?)$/gm, '<h1 class="text-2xl font-bold text-white mt-3 mb-1">$1</h1>');
    
    formatted = formatted.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-400 hover:text-cyan-300 underline">$1</a>');
    
    const lines = formatted.split('\n');
    const processedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.includes('<ul') || line.includes('</ul>') || 
          line.includes('<li') || line.includes('<h') || 
          line.includes('<strong') || line.includes('<em') ||
          line.includes('<a')) {
        processedLines.push(line);
      } 
      else if (line.trim() === '') {
        continue;
      }
      else {
        processedLines.push(`<p class="mb-1 last:mb-0 text-white/90">${line}</p>`);
      }
    }
    
    formatted = processedLines.join('\n');
    formatted = formatted.replace(/\n\s*\n/g, '\n');
    
    return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const TrainingCard = ({ data, onClick }) => {
    const trainingData = data.fullData || data.data;
    
    return (
      <div className="w-full rounded-[20px] border p-4 flex items-center gap-4 bg-[#241352] border-white/20">
        <div className="w-[100px] h-[100px] rounded-[12px] overflow-hidden shrink-0 mr-2">
          <img
            src={trainingData.image || "https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80"}
            alt={trainingData.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-1.2.1&auto=format&fit=crop&w=400&q=80";
            }}
          />
        </div>

        <div className="flex-1 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[16px] leading-tight font-medium break-words whitespace-normal text-[#C8AFE8]">
                {trainingData.title || 'Тренинг'}
              </div>
              
              {trainingData.premium && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-300">
                  Premium
                </span>
              )}
            </div>
            
            <div className="text-[14px] font-medium text-[#34D3FF]">
              Доступно
            </div>
          </div>

          <button 
            onClick={onClick}
            className="px-6 py-2 rounded-full text-white text-[15px] font-medium active:scale-95 transition hover:opacity-90 bg-[#6B3FD8] w-fit"
          >
            Смотреть
          </button>
        </div>
      </div>
    );
  };

  const renderContent = (content) => {
    if (!content) return null;

    const contentKey = `${content.type}_${content.id}`;
    const enrichedContent = contentData[contentKey] || content;

    switch (content.type) {
      case 'video':
        let kinescopeId = null;
        
        if (enrichedContent?.data?.kinescopeId) {
          kinescopeId = enrichedContent.data.kinescopeId;
        } 
        else if (enrichedContent?.kinescopeId) {
          kinescopeId = enrichedContent.kinescopeId;
        }
        else if (enrichedContent?.fullData?.kinescopeId) {
          kinescopeId = enrichedContent.fullData.kinescopeId;
        }
        else if (content?.data?.kinescopeId) {
          kinescopeId = content.data.kinescopeId;
        }
        else if (content?.kinescopeId) {
          kinescopeId = content.kinescopeId;
        }

        return (
          <div className="relative pb-[56.25%] h-0 bg-black rounded-3xl overflow-hidden">
            {kinescopeId ? (
              <iframe
                src={`https://kinescope.io/embed/${kinescopeId}`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                title={`video-${content.id}`}
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 bg-[#2A1B4E]/50 p-4 text-center">
                <div>Видео не найдено</div>
                <div className="text-xs mt-2 opacity-50">
                  ID: {content.data?.kinescopeId || content.kinescopeId || 'не указан'}
                </div>
              </div>
            )}
          </div>
        );

      case 'audio':
        const audioData = enrichedContent.fullData || content.data;
        
        if (audioData?.audioFiles && audioData.audioFiles.length > 0) {
          return (
            <div className="space-y-3">
              {audioData.audioFiles.map((audio, idx) => (
                <AudioPlayer 
                  key={idx}
                  audioData={audio}
                  onPlay={handleAudioPlay}
                />
              ))}
            </div>
          );
        }
        
        return (
          <AudioPlayer 
            audioData={{
              url: audioData?.audioUrl || audioData?.url,
              name: audioData?.title || audioData?.name,
              premium: audioData?.premium
            }}
            onPlay={handleAudioPlay}
          />
        );

      case 'training':
        return (
          <TrainingCard 
            data={enrichedContent}
            onClick={() => {
              if (content.data?.id) {
                navigate(`/training/${content.data.id}`);
              }
            }}
          />
        );

      case 'masterclass':
        return (
          <TrainingCard 
            data={enrichedContent}
            onClick={() => {
              if (content.data?.id) {
                navigate(`/master-class/${content.data.id}`);
              }
            }}
          />
        );

      case 'button':
        return (
          <div className="rounded-3xl p-6 text-center">
            <a
              href={content.data?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-[#6B4CF6] to-[#F06CB7] text-white font-semibold hover:opacity-90 transition"
            >
              {content.data?.text || 'Перейти'}
            </a>
          </div>
        );

      case 'text':
        return (
          <div className="bg-[#2A1B4E]/90 rounded-3xl p-6">
            <div className="text-white/90 leading-relaxed">
              {formatText(content.data?.text)}
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-[#2A1B4E]/90 rounded-3xl p-6 text-center text-white/70">
            Неподдерживаемый тип контента: {content.type}
          </div>
        );
    }
  };

  const renderSlideContents = (slide) => {
    if (!slide.contents || slide.contents.length === 0) {
      return (
        <div className="relative pb-[56.25%] h-0">
          <div className="absolute inset-0 flex items-center justify-center text-white/70 bg-[#2A1B4E]/50 rounded-3xl">
            Нет контента
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {slide.contents.map((content, index) => (
          <div key={content.id || index}>
            {renderContent(content)}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-[#241352] text-white flex items-center justify-center">
        <div className="text-2xl">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full bg-[#241352] text-white flex flex-col items-center justify-center gap-4">
        <div className="text-2xl">{error}</div>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 rounded-full bg-[#6B3FD8] text-white font-medium"
        >
          Вернуться назад
        </button>
      </div>
    );
  }

  if (!miniPath) {
    return (
      <div className="h-screen w-full bg-[#241352] text-white flex flex-col items-center justify-center gap-4">
        <div className="text-2xl">Мини-путь не найден</div>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 rounded-full bg-[#6B3FD8] text-white font-medium"
        >
          Вернуться назад
        </button>
      </div>
    );
  }

  const totalPages = miniPath.slides?.length || 0;

  return (
    <div className="relative h-screen w-full overflow-hidden text-white">
      {/* BACKGROUND */}
      {currentPage === 0 && (
        <div
          className="fixed inset-0"
          style={{ 
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}

      {/* SLIDER CONTAINER */}
      <div className="relative h-full w-full overflow-hidden">
        {/* SLIDER */}
        <div
          className="flex h-full transition-transform duration-500 ease-in-out"
          style={{
            width: `${totalPages * 100}%`,
            transform: `translateX(-${currentPage * (100 / totalPages)}%)`,
          }}
        >
          {miniPath.slides?.map((slide, index) => (
            <div
              key={index}
              className="relative h-full"
              style={{ width: `${100 / totalPages}%` }}
            >
              {/* Контент слайда с внутренним скроллом */}
              <div
                className="h-full overflow-y-auto px-6 pb-10 pt-6"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {/* HEADER */}
                <div className="mt-4">
                  <div className="text-2xl font-bold tracking-wide mb-4">
                    {miniPath.title || 'МИНИ ПУТЬ'}
                  </div>

                  {/* БЛОК ПЕРЕКЛЮЧЕНИЯ СЛАЙДОВ */}
                  <div className="flex justify-center mb-6">
                    <div className="flex items-center gap-4">
                      {currentPage > 0 && (
                        <button
                          onClick={prevPage}
                          className="w-[56px] h-[32px] flex items-center justify-center active:scale-95 transition rotate-180 z-20"
                        >
                          <img src={arrowIcon} alt="previous" className="w-full h-full" />
                        </button>
                      )}
                      
                      <div className="flex items-center gap-2 h-10">
                        {[...Array(currentPage)].map((_, i) => (
                          <div key={`left-${i}`} className="w-2.5 h-2.5 rounded-full bg-white/60" />
                        ))}
                        
                        <div className="w-10 h-10 rounded-full bg-[#6B4CF6] flex items-center justify-center text-lg font-semibold">
                          {currentPage + 1}
                        </div>

                        {[...Array(totalPages - currentPage - 1)].map((_, i) => (
                          <div key={`right-${i}`} className="w-2.5 h-2.5 rounded-full bg-white/60" />
                        ))}
                      </div>

                      {currentPage < totalPages - 1 && (
                        <button
                          onClick={nextPage}
                          className="w-[56px] h-[32px] flex items-center justify-center active:scale-95 transition z-20"
                        >
                          <img src={arrowIcon} alt="next" className="w-full h-full" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="text-lg italic leading-snug mb-6 text-center">
                    {slide.text || `Текст ${index + 1} слайда`}
                  </div>
                </div>

                {/* TEXT CARD */}
                <div className="bg-[#4C2B8F]/90 rounded-3xl p-6 mb-6 backdrop-blur-md">
                  <div className="text-base leading-relaxed text-white/90">
                    {formatText(slide.cardText)}
                  </div>
                </div>

                {/* CONTENT */}
                <div className="relative rounded-3xl">
                  {renderSlideContents(slide)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}